/**
 * Frontend permission service — mirrors backend permission constants
 * and fetches the current user's resolved permissions from the server.
 *
 * Usage:
 *   await loadPermissions();           // call once at login
 *   hasPermission(PERMISSIONS['queue.view']);  // check anywhere
 */

import { apiGet } from './api';

// ---------------------------------------------------------------------------
// Permission constants — mirrors src/lib/auth/permissions.ts exactly
// ---------------------------------------------------------------------------

export const PERMISSIONS = {
  // Queue
  'queue.view': 'queue.view',
  'queue.priority': 'queue.priority',
  'queue.dashboard': 'queue.dashboard',
  'queue.reassign': 'queue.reassign',

  // Console
  'console.view': 'console.view',
  'console.templates.manage': 'console.templates.manage',
  'console.queue_item.update': 'console.queue_item.update',

  // Contacts
  'contacts.view': 'contacts.view',
  'contacts.create': 'contacts.create',
  'contacts.export': 'contacts.export',
  'contacts.bulk_queue': 'contacts.bulk_queue',
  'contacts.bulk_assign': 'contacts.bulk_assign',
  'contacts.bulk_edit': 'contacts.bulk_edit',
  'contacts.bulk_brand': 'contacts.bulk_brand',
  'contacts.bulk_terminal': 'contacts.bulk_terminal',

  // Inbox
  'inbox.view': 'inbox.view',

  // Notifications — opt in to tenant-wide inbound message notifications
  // instead of the assigned-contacts default (#1541). Role floor is
  // tenant-configurable; the server resolver applies it.
  'notifications.team': 'notifications.team',

  // Admin — Notification behavior settings
  'admin.notifications.manage': 'admin.notifications.manage',

  // Voice
  'voice.view': 'voice.view',

  // Dashboard / KPI
  'dashboard.view': 'dashboard.view',
  'dashboard.view_team': 'dashboard.view_team',

  // Exams
  'exams.view': 'exams.view',

  // Flags
  'flags.view': 'flags.view',

  // Scholarship
  'scholarship.view': 'scholarship.view',

  // Support
  'support.view': 'support.view',

  // Vendor
  'vendor.view': 'vendor.view',

  // Media
  'media.view': 'media.view',

  // Compliance
  'compliance.view': 'compliance.view',

  // Reporting
  'reporting.view': 'reporting.view',

  // Rules
  'rules.view': 'rules.view',
  'rules.evaluate': 'rules.evaluate',
  'rules.rebuild': 'rules.rebuild',

  // Admin — Rep Management
  'admin.reps.view': 'admin.reps.view',
  'admin.reps.delete': 'admin.reps.delete',
  'admin.agency_owners.manage': 'admin.agency_owners.manage',
  'admin.templates.manage': 'admin.templates.manage',
  'admin.ao_manager.manage': 'admin.ao_manager.manage',

  // Admin — Agencies
  'admin.agencies.view': 'admin.agencies.view',
  'admin.agencies.manage': 'admin.agencies.manage',
  'admin.agencies.move': 'admin.agencies.move',

  // Admin — Operations
  'admin.ops.view': 'admin.ops.view',
  'admin.ops.bulk_status_update': 'admin.ops.bulk_status_update',
  'admin.ops.sync_runs.read': 'admin.ops.sync_runs.read',
  'admin.ops.sync_runs.run': 'admin.ops.sync_runs.run',

  // Admin — System
  'admin.system.view': 'admin.system.view',

  // Admin — System audit trail (audit_log table)
  'admin.audit_log.view': 'admin.audit_log.view',

  // Admin — Cache
  'admin.cache.manage': 'admin.cache.manage',

  // Admin — A2P
  'admin.a2p.manage': 'admin.a2p.manage',

  // Admin — Messaging (platform-ops only)
  'admin.messaging.manage': 'admin.messaging.manage',

  // Admin — Voice Config (platform-ops only)
  'admin.voice_config.manage': 'admin.voice_config.manage',

  // Data Intake
  'data_intake.manage': 'data_intake.manage',

  // Duplicates
  'duplicates.manage': 'duplicates.manage',

  // Vendor Import
  'vendor_import.manage': 'vendor_import.manage',

  // Vendor Sync (/api/admin/vendor-sync)
  'admin.vendor_sync.manage': 'admin.vendor_sync.manage',

  // Admin — Scheduled Jobs (/api/admin/jobs) — csuite+ manual cron trigger
  'admin.jobs.run': 'admin.jobs.run',

  // Admin — Queue bulk bucket assign (/api/queue/bulk-assign-by-bucket) — csuite+
  'admin.queue.bulk_bucket_assign': 'admin.queue.bulk_bucket_assign',

  // Admin — Rep status reasons (/api/admin/rep-status-reasons) — #496 Phase C.2
  'admin.rep_status_reasons.manage': 'admin.rep_status_reasons.manage',

  // Admin — Tenant status policy (/api/admin/tenant-status-policy) — what
  // each status allows (outbound, inbound, transfer-target).
  'admin.tenant_status_policy.manage': 'admin.tenant_status_policy.manage',

  // Admin — TCPA state calling rules editor (/api/admin/calling-rules) — writes
  // are platform-ops only; reads ride on admin.ops.view
  'admin.calling_rules.manage': 'admin.calling_rules.manage',

  // Admin — Force another rep's state (/api/admin/rep-status/:userId/force) — #496
  'admin.rep_status.force_change': 'admin.rep_status.force_change',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// ---------------------------------------------------------------------------
// State — populated by loadPermissions()
// ---------------------------------------------------------------------------

let userPermissions: Set<string> = new Set();
let userRole = 'rep';
// Tenant notification behavior (#1541), delivered alongside permissions.
// Default matches the tenant_settings column default.
let unassignedToScoped = true;

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

interface PermissionsResponse {
  role: string;
  permissions: string[];
  overrides: Record<string, boolean>;
  notifications_unassigned_to_scoped?: boolean;
}

/**
 * Fetch the current user's resolved permissions from the server.
 * Call once during login/init — subsequent calls refresh the cache.
 */
export async function loadPermissions(): Promise<void> {
  try {
    const data = await apiGet<PermissionsResponse>('/auth/permissions');
    userPermissions = new Set(data.permissions);
    userRole = data.role;
    unassignedToScoped = data.notifications_unassigned_to_scoped ?? true;
  } catch (err) {
    console.error('[Permissions] Failed to load permissions:', err);
    // Fallback: empty permissions — UI will be restrictive
    userPermissions = new Set();
    userRole = 'rep';
  }
}

/** Check if the current user has a specific permission. */
export function hasPermission(permission: string): boolean {
  return userPermissions.has(permission);
}

/** Get the current user's role (as resolved by the server). */
export function getUserRole(): string {
  return userRole;
}

/**
 * Whether unassigned-contact messages notify assigned-scoped viewers (#1541).
 * Tenant setting, delivered with /auth/permissions.
 */
export function unassignedNotifiesScoped(): boolean {
  return unassignedToScoped;
}
